import { useTranslation } from 'react-i18next'
import SVG from 'react-inlinesvg'
import { useEffect, useRef } from 'react'
import { useContextPanels } from '../../contexts/panelsContext'
import { UIElement } from '../../types/UIElements'
import { useEnv } from '../../utils/useEnv'
import { cn } from '../../utils/cn'

const explanationItemClassName =
  'flex h-[45px] w-[165px] items-center justify-end rounded-[16px] border border-solid border-white max-sm:w-[140px] md:h-[60px] md:w-[190px] xl:h-[65px] xl:w-[245px]'

const roadmapStepClassName =
  'mb-0 ml-[10px] mr-[10px] mt-[10px] h-[35px] w-[35px] rounded-[50%] border border-solid border-white text-center font-approach-medium text-[1.8em] leading-[35px] md:mb-0 md:ml-[15px] md:mr-[15px] md:mt-[15px] md:h-[40px] md:w-[40px] md:leading-[40px] xl:mb-0 xl:ml-[20px] xl:mr-[20px] xl:mt-[25px] xl:h-[45px] xl:w-[45px] xl:leading-[45px]'

const itemTextClassName =
  'whitespace-pre-wrap text-right text-[1.125em] leading-[16px] tracking-[0.69px] text-white max-sm:text-[1em] max-sm:tracking-[0] md:leading-[18px] xl:leading-[22px]'

const descriptionTextClassName =
  'px-[30px] py-0 leading-[25px] max-sm:pb-0 max-sm:pl-0 max-sm:pr-[10px] max-sm:pt-0 max-sm:leading-[20px] md:px-[30px] md:py-0 md:text-[18px] md:leading-[30px] xl:px-[30px] xl:py-0 xl:text-[21px] xl:leading-[36px]'

const ExplanationFeature = () => {
  const { t } = useTranslation()
  const { isPanelOpen, closePanel } = useContextPanels()
  const { isKiosk } = useEnv()
  const isOpen = isPanelOpen(UIElement.EXPLANATION)
  const wasOpen = useRef(isOpen)

  // anything can close the explanation (the side menu button stays above it), the address follows
  useEffect(() => {
    if (wasOpen.current && !isOpen) window.history.replaceState(null, '', '/')
    wasOpen.current = isOpen
  }, [isOpen])

  const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const el = e.target as HTMLElement
    if (el.classList.contains('outside')) {
      closePanel(UIElement.EXPLANATION)
    }
  }

  return (
    <>
      <div
        className={cn(
          'outside absolute left-0 top-0 flex h-full w-full items-center justify-center [transform:translateX(-100%)] [transition:0.3s_ease-out] [&.slide-in]:[transform:translateX(calc(50vw-50%))]',
          { 'slide-in': isOpen },
        )}
        onClick={e => handleOutsideClick(e)}
      >
        <div
          className="absolute top-[2vh] flex h-[500px] w-[700px] flex-col items-center rounded-[17px] bg-black95 font-approach-light text-[10px] text-white shadow-floating tall-xs:top-[10vh] max-sm:h-[315px] max-sm:w-[525px] md:top-[8vh] md:h-[500px] md:w-[775px] md:text-[11px] xl:top-[8vh] xl:h-[600px] xl:w-[1010px] xl:text-[14px] xxl:top-[13vh]"
          onClick={() => closePanel(UIElement.EXPLANATION)}
        >
          <div className="relative flex h-[30px] w-[95%] flex-1 items-center pl-[30px] max-sm:max-h-[65px] max-sm:w-[90%] max-sm:pl-0 sm:max-h-[125px] md:max-h-[135px] xl:max-h-[145px]">
            <div>
              <span className="text-[25px] max-sm:text-[20px] md:text-[30px]">
                {t('howWorks')}
                {' – '}
              </span>
              <span className="font-approach-bold text-[25px] max-sm:text-[20px] md:text-[30px]">
                {t('chooseAndCombine')}
              </span>
            </div>
          </div>
          <div className="w-[95%] flex-1 max-sm:w-[90%] sm:mt-[35px] md:mt-[10px] xl:mt-[45px]">
            <div className="mb-[25px] flex justify-center">
              <p className={roadmapStepClassName}>1</p>
              <SVG
                className="h-[25px] w-[140px] md:h-[35px] md:w-[170px] xl:h-[50px] xl:w-[215px]"
                src="/icons/dots.svg"
              />
              <p className={roadmapStepClassName}>2</p>
              <SVG
                className="h-[25px] w-[140px] md:h-[35px] md:w-[170px] xl:h-[50px] xl:w-[215px]"
                src="/icons/dots.svg"
              />
              <p className={roadmapStepClassName}>3</p>
            </div>
            <div className="flex content-center gap-[30px] px-[30px] py-0 font-approach-light text-[11px] max-sm:p-0 md:gap-[55px] md:px-[30px] md:py-0 md:text-[12px] xl:gap-[78px] xl:px-[30px] xl:py-0 xl:text-[14px]">
              <div className={explanationItemClassName}>
                <p className={itemTextClassName}>{t('chooseMotifs')}</p>
                <SVG
                  src="/icons/heart2.svg"
                  className="mb-0 ml-[5px] mr-[8px] mt-0 h-[20px] w-[20px] opacity-80 md:mb-0 md:ml-[7px] md:mr-[12px] md:mt-0 md:h-[25px] md:w-[25px] xl:mb-0 xl:ml-[10px] xl:mr-[13px] xl:mt-0 xl:h-[30px] xl:w-[30px]"
                />
              </div>
              <div className={explanationItemClassName}>
                <p className={itemTextClassName}> {t('choosePattern')}</p>
                <SVG
                  src="/icons/patterns.svg"
                  className="mb-0 ml-[5px] mr-[8px] mt-0 h-[20px] w-[20px] opacity-80 md:mb-0 md:ml-[7px] md:mr-[12px] md:mt-0 md:h-[25px] md:w-[25px] xl:mb-0 xl:ml-[10px] xl:mr-[13px] xl:mt-0 xl:h-[30px] xl:w-[30px]"
                />
              </div>
              <div className={explanationItemClassName}>
                <p className={itemTextClassName}>{t('downloadAndShare')}</p>
                <SVG
                  src="/icons/download.svg"
                  className="mb-0 ml-[5px] mr-[8px] mt-0 h-[20px] w-[20px] opacity-80 md:mb-0 md:ml-[7px] md:mr-[12px] md:mt-0 md:h-[25px] md:w-[25px] xl:mb-0 xl:ml-[10px] xl:mr-[13px] xl:mt-0 xl:h-[30px] xl:w-[30px]"
                />
              </div>
            </div>
          </div>
          <div className="flex w-[95%] flex-1 items-center justify-center gap-[30px] text-[16px] max-sm:w-[90%] sm:mb-[50px] md:mb-[25px] xl:mb-[35px]">
            <p className={descriptionTextClassName}>
              {isKiosk ? t('moveMotifMapKiosk') : t('moveMotifMap')}
            </p>
            <p className={descriptionTextClassName}>
              {isKiosk ? t('choosePatternFromElementsKiosk') : t('choosePatternFromElements')}
            </p>
          </div>
        </div>
      </div>
    </>
  )
}

export default ExplanationFeature
